import React, { useEffect, useState } from "react";
import BreadcrumbItem from "../../../shared/components/BreadcrumbItem";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IProtocol } from "../../../shared/interfaces/protocol/IProtocol";
import { WebProtocolService } from "./service";
import { handleError } from "../../../shared/functions/handleError";
import { usePagination } from "../../../shared/hooks/usePagination";
import OperatorProtocolList from "./components/List";
import { Button, Card, CardHeader, Col, Form, Row } from "react-bootstrap";
import { IPagination } from "../../../shared/interfaces/shared/IPagination";
import Select2 from "../../../shared/components/Select2";
import { WebIncidentService } from "../incidents/service";

const OperatorProtocols = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [protocols, setProtocols] = React.useState<IProtocol[]>([]);
  const [incidentTypes, setIncidentTypes] = useState<string[]>([]);

  const {
    pagination,
    setPagination,
    changeFilterHandler,
    handleUpdateList,
    totalPages,
    setTotalPages,
  } = usePagination({
    perPage: 20,
  });

  const [localPagination, setLocalPagination] = useState<IPagination | null>(
    null,
  );

  const incidentTypeOptions = incidentTypes.map((type) => ({
    value: type,
    label: t(`incident.type.${type}`),
  }));

  const changeLocalPaginationHandler = (
    name: string,
    value: string | number | boolean | number[] | null,
  ) => {
    setLocalPagination((prev) => ({ ...prev, [name]: value }) as IPagination);
  };

  useEffect(() => {
    WebProtocolService.getAll(pagination)
      .then((response) => {
        setProtocols(response?.data);
        setTotalPages(response?.totalPages);
      })
      .catch(handleError);
  }, [pagination, setTotalPages]);

  useEffect(() => {
    WebIncidentService.getTypes()
      .then((response) => {
        setIncidentTypes(response?.data || []);
      })
      .catch(handleError);
  }, []);

  return (
    <React.Fragment>
      <BreadcrumbItem
        mainTitle={t("protocol.protocols")}
        subTitle={t("protocol.protocols")}
        buttonTitle={t("protocol.addProtocol")}
        onCtaClick={() => navigate("/protocols/create")}
      />
      <Card>
        <Card.Header>
          <h5>{t("protocol.filterProtocols")}</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Control
                placeholder={t("protocol.searchByTitle")}
                name={"title"}
                value={localPagination?.title ?? ""}
                onChange={(e) =>
                  changeLocalPaginationHandler(e.target.name, e.target.value)
                }
                className={"w-100"}
              />
            </Col>
            <Col md={4}>
              <Select2
                name="incidentType"
                placeholder={t("incident.selectIncidentType")}
                options={incidentTypeOptions}
                onChange={(event) => {
                  setLocalPagination(
                    (prev) => ({ ...prev, incidentType: event }) as IPagination,
                  );
                }}
                value={localPagination?.incidentType}
              />
            </Col>
            <Col md={4}>
              <Form.Control
                placeholder={t("beacon.searchByUuid")}
                name={"beaconUuid"}
                value={localPagination?.beaconUuid ?? ""}
                onChange={(e) =>
                  changeLocalPaginationHandler(e.target.name, e.target.value)
                }
              />
            </Col>
          </Row>
        </Card.Body>
        <Card.Footer className={"text-end"}>
          <Button
            variant={"light"}
            onClick={() => {
              setLocalPagination(null);
              setPagination({ page: 0, size: 20 });
            }}
          >
            {t("button.reset")}
          </Button>
          <Button
            variant={"primary"}
            className={"ms-3"}
            onClick={() => {
              setPagination((prev: IPagination) => ({
                ...prev,
                ...localPagination,
                page: 0,
              }));
            }}
          >
            {t("button.search")}
          </Button>
        </Card.Footer>
      </Card>
      <Card className="table-card">
        <CardHeader>
          <h5>{t("protocol.protocolList")}</h5>
        </CardHeader>

        <Card.Body className="pt-3">
          <OperatorProtocolList
            protocols={protocols}
            pagination={pagination}
            totalPages={totalPages}
            handleUpdateList={handleUpdateList}
            changeFilterHandler={changeFilterHandler}
          />
        </Card.Body>
      </Card>
    </React.Fragment>
  );
};

export default OperatorProtocols;
